import {
  ChangeDetectorRef,
  Component,
  effect,
  HostBinding,
  OnDestroy,
  OnInit,
  signal,
  inject,
  WritableSignal,
} from '@angular/core';
import {
  FieldArrayType,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { SessionStorageService } from '../../../services/session-storage.service';
import { InvestmentWizardService } from '../../../investment-wizard/services/investment-wizard-service.service';
import { MaskService } from '../../../services/mask.service';
import { FormService } from '../../form-service.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { FormCalculatorComponent } from '../form-calculator/form-calculator.component';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'app-formly-repeat-section',
  styleUrls: ['./form-repeating.component.scss'],
  templateUrl: `./form-repeating.component.html`,
  imports: [
    FormCalculatorComponent,
    MatIconModule,
    FormlyModule,
    CurrencyPipe,
    CommonModule,
    MatTooltipModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class FormlyRepeatingComponent
  extends FieldArrayType
  implements OnInit, OnDestroy
{
  private ss = inject(SessionStorageService);
  private detector = inject(ChangeDetectorRef);
  private iws = inject(InvestmentWizardService);
  private formSvc = inject(FormService);
  private mask = inject(MaskService);

  @HostBinding('class') classes = 'w-100 form-section';

  needsRowId = false;
  rowIdPrefix: string;
  tspRowIdPrefix: string;
  idVar: string;
  tspIdVar: string;
  parentIdVar: string;
  tspParentIdVar: string;
  disableAdd = false;
  maxEntries = 0;
  minEntries = false;
  mode: string;
  program: any;
  amountAllocationTotal = 0;
  amountAllocatedVarNames: any;
  amountAllocated: number;
  amountAvailableVarNames: any;
  amountAvailable: number;
  amountLeft: number;
  updateExternalCalcValue = signal(null);
  triggerIncomeGroupChangeObs;
  unsubscribe = new Subject();
  additionalAddData: { key: string; value: string }[];
  amountAllocatedSubVarNames: any;
  percentLeft: number;

  constructor() {
    super();
    this.updateExternalCalcValue = this.formSvc.updateExternalCalcValue;

    effect(() => {
      if (this.updateExternalCalcValue() && this.field['showCalculator']) {
        if (this.ss.get('currentApp') === 'iw') {
          this.getAndCalcAmountIW();
        } else if (this.ss.get('currentApp') === 'pp') {
          this.getAndCalcAmountPP();
        }
      }
    });
  }

  private processSpouseInfo(
    martialStatus: string,
    includeSpouse: string
  ): void {
    const spouseItems = this.model.filter(entry => {
      return entry.HouseholdIncome_Owner === 'Spouse';
    });
    if (
      spouseItems.length === 0 &&
      martialStatus === 'M' &&
      includeSpouse === 'Y'
    ) {
      this.additionalAddData = [
        { key: 'HouseholdIncome_Owner', value: 'Spouse' },
      ];
      if (
        this.field.fieldArray['props'].defaultOpen ||
        this.field.fieldArray['props'].minEntries
      ) {
        this.add();
      }
      this.additionalAddData = [];
    }
    this.detector.detectChanges();
  }

  ngOnDestroy(): void {
    this.unsubscribe.next(null);
    this.unsubscribe.complete();
  }

  ngOnInit() {
    this.onPopulate(this.field);
    this.triggerIncomeGroupChangeObs = this.formSvc.triggerIncomeGroupChange$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(
        (x: {
          maritalStatus: string;
          spouseEmployment: string;
          includeSpouse: string;
        }) => {
          if (
            this.ss.get('currentApp') === 'rb' &&
            this.field.key === 'income'
          ) {
            this.processSpouseInfo(x.maritalStatus, x.includeSpouse);
          }
        }
      );

    const globals = this.ss.get('globals');

    switch (this.key) {
      case 'Five29Plans':
        this.maxEntries = globals.GlobalsGroup?.UnitSettings?.MaxSelectedPlans;
        break;
      case 'jointOwner':
        this.maxEntries =
          globals.GlobalsGroup?.UnitSettings?.MaxJointOwners &&
          globals.GlobalsGroup?.UnitSettings?.MaxJointOwners > 0
            ? globals.GlobalsGroup?.UnitSettings?.MaxJointOwners
            : 9999;
        break;
      case 'entityJointOwner':
        this.maxEntries =
          globals.GlobalsGroup?.UnitSettings?.MaxEntityJointOwners &&
          globals.GlobalsGroup?.UnitSettings?.MaxEntityJointOwners > 0
            ? globals.GlobalsGroup?.UnitSettings?.MaxEntityJointOwners
            : 9999;
        break;
      default:
        this.maxEntries = globals.GlobalsGroup?.UnitSettings?.MaxSelectProducts;
        break;
    }

    if (
      (this.field.fieldArray['props'].defaultOpen ||
        this.field.fieldArray['props'].minEntries) &&
      this.field.model.length === 0
    ) {
      this.add();
    }

    const vars = this.field.fieldArray['fieldGroup'].map(x => x.key);
    const parentVars = this.field.parent.fieldGroup.map(x => x.key);
    this.parentIdVar = parentVars.find((x: string) =>
      x.includes('SourceBridgeID')
    ) as string;

    this.tspParentIdVar = parentVars.find((x: string) =>
      x.includes('TSPSourceID')
    ) as string;

    this.idVar = vars.find((x: string) =>
      x.includes('SourceBridgeID')
    ) as string;
    this.tspIdVar = vars.find((x: string) =>
      x.includes('TSPSourceID')
    ) as string;

    if (this.idVar || this.tspIdVar) {
      this.needsRowId = true;
      this.rowIdPrefix = this.idVar ? this.idVar.split('_')[0] : this.idVar;
      this.tspRowIdPrefix = this.tspIdVar
        ? this.tspIdVar.split('_')[0]
        : this.tspIdVar;
    }

    if (
      this.field.fieldArray['props'].disableCheck ||
      (this.field.fieldArray['props'].entryLimit &&
        this.maxEntries > 0 &&
        this.field.model.length >= this.maxEntries)
    ) {
      this.disableAdd = true;
      this.detector.detectChanges();
    }

    if (this.needsRowId || this.field.key === 'exchange') {
      this.field.fieldGroup.forEach((group, idx) => {
        group.fieldGroup.forEach(x => {
          if (x.key === 'CurrentAnnuityDetailsArray_IsExchange') {
            group['isExchange'] = x.formControl.value;
          }

          const fieldKey = x.key as string;

          if (fieldKey.includes('SourceBridgeID')) {
            if (x.formControl && !x.formControl.value) {
              x.formControl.setValue(`${this.rowIdPrefix}-${idx}`);
            }
          }

          if (fieldKey.includes('TSPSourceID')) {
            if (x.formControl && !x.formControl.value) {
              x.formControl.setValue(`TSPFundDetails-${idx}`);
            }
          }
        });
      });
    }
    if (this.field['showCalculator']) {
      this.formSvc.needToUpdateCalc = true;
      if (this.ss.get('currentApp') === 'iw') {
        this.mode = this.field.parent.model.InvestmentValidation_PickPctOrAmt;
        this.getAndCalcAmountIW();
      } else if (this.ss.get('currentApp') === 'pp') {
        this.mode = 'A';
        this.getAndCalcAmountPP();
      }
    }

    if (
      this.ss.get('currentApp') === 'rb' &&
      this.field.key === 'income' &&
      this.field.parent.model?.['ClientPerson_MaritalStatus'] === 'M'
    ) {
      this.processSpouseInfo(
        this.field.parent.model?.['ClientPerson_MaritalStatus'],
        this.field.parent.model?.['SpousePerson_Include']
      );
    }

    this.field.form.valueChanges
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(z => {
        if (this.formSvc.getNeedToUpdateCalc()) {
          this.formSvc.setUpdateExternalCalcValue({ source: 'formRepeating' });
        }
        if (z.InvestmentValidation_PickPctOrAmt === 'P') {
          this.mode = 'percent';
        } else {
          this.mode = 'amount';
        }
      });
    this.field.options.build();
  }

  getAndCalcAmountIW() {
    if (this.field['amountAvailable']) {
      this.amountAvailableVarNames = this.field['amountAvailable'].split('.');
      let amountAvailableTracker = this.field;
      this.amountAvailableVarNames.map(varName => {
        amountAvailableTracker = amountAvailableTracker[varName];
      });
      this.amountAvailable = this.mask.removeMoneyMask(amountAvailableTracker);
    } else {
      this.amountAvailable = this.calculateTotalAmount();
    }
    if (
      this.mode.toLowerCase() === 'a' ||
      this.mode.toLowerCase() === 'amount'
    ) {
      this.amountAllocatedVarNames = this.field['amountAllocated'].split('|');
      this.amountAllocated = 0;
      this.amountAllocatedVarNames.forEach(varName => {
        this.amountAllocatedSubVarNames = varName.split('.');
        this.amountAllocated += +this.stepToAmount(this.field, 0);
      });
      this.amountLeft = this.amountAvailable - this.amountAllocated;
    } else if (
      this.mode.toLowerCase() === 'p' ||
      this.mode.toLowerCase() === 'percent'
    ) {
      this.amountAllocatedVarNames = this.field['percentAllocated'].split('|');
      this.amountAllocated = 0;
      this.amountAllocatedVarNames.forEach(varName => {
        this.amountAllocatedSubVarNames = varName.split('.');
        this.amountAllocated += +this.stepToAmount(this.field, 0);
      });
      this.percentLeft = 100 - +this.amountAllocated;
    }
    this.detector.detectChanges();
  }

  calculateTotalAmount() {
    let totalAmount = 0;
    if (
      (this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === 2 ||
        this.field.parent.model.ValidateProduct
          .InvestmentValidation_PurchaseType === '2') &&
      this.field.parent.model.ValidateProduct.InvestmentWizard_AddMoneyAmount
    ) {
      totalAmount = this.mask.removeMoneyMask(
        this.field.parent.model.ValidateProduct.InvestmentWizard_AddMoneyAmount
      );
    } else if (
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === 3 ||
      this.field.parent.model.ValidateProduct
        .InvestmentValidation_PurchaseType === '3'
    ) {
      let totalDisplay = 0;
      this.field.parent.model.CurrentPosition.forEach(investment => {
        if (
          ((investment &&
            ((Object.prototype.hasOwnProperty.call(
              investment,
              'CurrentPosition_Sell'
            ) &&
              investment.CurrentPosition_Sell === 'true') ||
              investment.CurrentPosition_Sell === true)) ||
            (investment &&
              Object.prototype.hasOwnProperty.call(
                investment,
                'CurrentPosition_HoldOrSell'
              ) &&
              investment.CurrentPosition_HoldOrSell === 'Sell')) &&
          (investment.CurrentPosition_SellAmount ||
            investment.CurrentPosition_SellAmount === 0)
        ) {
          totalDisplay += +this.mask.removeMoneyMask(
            investment.CurrentPosition_SellAmount
          );
        }
      });
      totalAmount = totalDisplay;
    } else if (
      this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount
    ) {
      totalAmount = this.mask.removeMoneyMask(
        this.field.parent.model.ValidateProduct.InvestmentWizard_AssetAmount
      );
    }
    return totalAmount;
  }

  getAndCalcAmountPP() {
    try {
      this.amountAllocated = 0;
      this.amountAllocatedVarNames = this.field['amountAllocated'].split('.');
      let amountAllocatedTracker = null;
      this.amountAllocatedVarNames.map(varName => {
        if (varName === 'model' || varName === 'localmodel') {
          amountAllocatedTracker = this.model;
        } else {
          amountAllocatedTracker.forEach(tracker => {
            amountAllocatedTracker = tracker[varName];
            if (
              this.mask.removeMoneyMask(amountAllocatedTracker) !== undefined &&
              typeof +this.mask.removeMoneyMask(amountAllocatedTracker) ===
                'number'
            ) {
              this.amountAllocated += +this.mask.removeMoneyMask(
                amountAllocatedTracker
              );
            }
          });
        }
      });

      this.amountAvailable = 0;
      this.amountAvailableVarNames = this.field['amountAvailable'].split('|');
      const showAmountAvailableVarNames =
        this.field.fieldArray['props']?.['showAmountAvailable'];

      // Recursive function to process amountAvailableTracker
      const processTracker = (tracker, varNames, varIndex) => {
        if (
          tracker &&
          (typeof tracker === 'number' || typeof tracker === 'string')
        ) {
          // If tracker is a number, process it
          this.checkHiddenAndAdd(
            showAmountAvailableVarNames,
            varIndex,
            tracker
          );
          return;
        }

        if (Array.isArray(tracker)) {
          // If tracker is an array, iterate through each item
          tracker.forEach(item => {
            const trackerKeys = Object.keys(item);
            let nextVarName = varNames[0];
            let tempNextVarName = `${nextVarName.split('_')[0]}_${
              item[
                trackerKeys.find(trackerKey =>
                  trackerKey.toLowerCase().includes('fullvspartial')
                )
              ] === 'P'
                ? 'PartialAmount'
                : 'CurrentValue'
            }`;
            if (!Object.prototype.hasOwnProperty.call(item, tempNextVarName)) {
              tempNextVarName = nextVarName;
            }
            nextVarName = tempNextVarName;
            varNames[0] = nextVarName;
            processTracker(item, varNames, varIndex);
          });
          return;
        }
        if (
          typeof tracker === 'object' &&
          tracker !== null &&
          varNames.length > 0
        ) {
          // If tracker is an object, progress to the next reference
          const nextVarName = varNames[0]; // Get the next variable name
          if (nextVarName === 'model') {
            processTracker(
              this.field.parent.model,
              varNames.slice(1),
              varIndex
            ); // Pass a copy of varNames without modifying the original
          } else {
            processTracker(tracker[nextVarName], varNames.slice(1), varIndex); // Pass a copy of varNames without modifying the original
          }
        }
      };

      // Iterate through each varGroup
      this.amountAvailableVarNames.forEach((varGroup, varIndex) => {
        const currVarNames = varGroup.split('.'); // Split the varGroup into references
        processTracker(this.field, currVarNames, varIndex); // Start processing
      });
      this.amountLeft = this.amountAllocated - this.amountAvailable;
      this.detector.detectChanges();
    } catch (error) {
      console.error('Error parsing references', error);
    }
  }

  checkHiddenAndAdd(
    showAmountAvailableVarNames,
    varIndex,
    amountAvailableTracker
  ) {
    let showLevel = null;
    if (showAmountAvailableVarNames) {
      // prettier-ignore
      const propertyChain = showAmountAvailableVarNames.split('|')[varIndex].split('.');

      propertyChain.forEach(property => {
        if (property === 'model') {
          showLevel = this.field.parent.model;
        } else {
          showLevel = showLevel[property];
        }
      });
      if (typeof showLevel === 'boolean' && showLevel) {
        this.amountAvailable += +this.mask.removeMoneyMask(
          amountAvailableTracker
        );
      }
    } else {
      throw new Error('No references to selected sources');
    }
  }

  stepToAmount(currLevel, varNameIndex): number {
    let runningTotal = 0;
    if (currLevel) {
      if (!Array.isArray(currLevel)) {
        runningTotal += +this.stepToAmount(
          currLevel[this.amountAllocatedSubVarNames[varNameIndex]],
          varNameIndex + 1
        );
      } else {
        currLevel.forEach(levelItem => {
          if (
            Array.isArray(
              levelItem[this.amountAllocatedSubVarNames[varNameIndex]]
            )
          ) {
            runningTotal += +this.stepToAmount(
              levelItem[this.amountAllocatedSubVarNames[varNameIndex]],
              varNameIndex + 1
            );
          } else {
            runningTotal += levelItem[
              this.amountAllocatedSubVarNames[varNameIndex]
            ]
              ? +this.mask.removeMoneyMask(
                  levelItem[this.amountAllocatedSubVarNames[varNameIndex]]
                )
              : 0;
          }
        });
      }
    }
    return runningTotal;
  }

  toggleControlSwitch(index) {
    const controlField = this.getField(
      this.field,
      'CurrentAnnuityDetailsArray_IsExchange',
      index
    );
    const theControl = controlField.formControl.value;
    controlField.formControl.setValue(!theControl);
    this.field.fieldGroup[index]['isExchange'] =
      !this.field.fieldGroup[index]['isExchange'];
  }

  add() {
    super.add();

    const row = this.model.length > 0 ? this.model.length - 1 : 0;
    if (this.field.key === 'exchange' && this.field.fieldGroup.length > 0) {
      this.field.fieldGroup[row]['isExchange'] = true;
    }

    if (this.needsRowId) {
      let existingRowValues = this.model.map(x => {
        try {
          if (this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y') {
            return +x[this.tspIdVar].split('_')[1];
          } else {
            return +x[this.idVar].split('_')[1];
          }
        } catch (error) {
          return null;
        }
      });

      existingRowValues = existingRowValues.filter(
        x => x !== null || x !== undefined
      );

      const rowPrefix =
        this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y'
          ? this.tspRowIdPrefix
          : this.rowIdPrefix;
      const highestIdx =
        existingRowValues.length > 0 ? Math.max(...existingRowValues) : null;
      let newIdx = 0;

      if (this.parentIdVar || this.tspParentIdVar) {
        newIdx = +this.field.parent.key;
      } else {
        newIdx = !highestIdx || row > highestIdx ? row : highestIdx + 1;
      }

      const parentIdField =
        this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y'
          ? this.getField(this.field, this.tspIdVar, row)
          : this.getField(this.field, this.idVar, row);

      if (this.field.parent.model.CurrentNonERISADetailsArray_TSP === 'Y') {
        if (!this.field.parent.model[this.tspParentIdVar]) {
          this.field.parent.model[this.tspParentIdVar] =
            `${rowPrefix}-${newIdx}`;
        }
      } else {
        if (!this.field.parent.model[this.parentIdVar]) {
          this.field.parent.model[this.parentIdVar] = `${rowPrefix}-${newIdx}`;
          parentIdField.formControl.setValue(`${rowPrefix}-${newIdx}`);
        }
      }

      if (this.needsRowId || this.field.key === 'exchange') {
        this.field.fieldGroup.forEach((group, idx) => {
          group.fieldGroup.forEach(x => {
            const fieldKey = x.key as string;

            if (fieldKey.includes('SourceBridgeID')) {
              if (!x.formControl.value) {
                x.formControl.setValue(`${this.rowIdPrefix}-${idx}`);
              }
            }

            if (fieldKey.includes('TSPSourceID')) {
              if (!x.formControl.value) {
                x.formControl.setValue(`TSPFundDetails-${idx}`);
              }
            }
          });
        });
      }
    }

    if (this.additionalAddData && this.field.key === 'income') {
      this.additionalAddData.forEach(dataPoint => {
        this.field.fieldGroup[this.field.fieldGroup.length - 1].fieldGroup
          .find(group => group.key === dataPoint.key)
          .formControl.setValue(dataPoint.value);
      });
    }

    if (
      this.field.fieldArray['props'].disableCheck ||
      (this.field.fieldArray['props'].entryLimit &&
        this.maxEntries > 0 &&
        this.field.model.length >= this.maxEntries)
    ) {
      this.disableAdd = true;
      this.detector.detectChanges();
    } else {
      this.disableAdd = false;
      this.detector.detectChanges();
    }
    this.onPopulate(this.field);
    this.options.build();
  }

  remove(index) {
    if (this.model[index] !== undefined) {
      Object.keys(this.model[index]).forEach(x => {
        if (Array.isArray(this.model[index][x])) {
          this.model[index][x] = [];
        }
      });
    }

    super.remove(index);

    if (
      this.field.fieldArray['props'].disableCheck ||
      (this.field.fieldArray['props'].entryLimit &&
        this.maxEntries > 0 &&
        this.field.model.length >= this.maxEntries)
    ) {
      this.disableAdd = true;
      this.detector.detectChanges();
    } else {
      this.disableAdd = false;
      this.detector.detectChanges();
    }
  }

  getField(field: FormlyFieldConfig, key, rowIndex: number): FormlyFieldConfig {
    return field.fieldGroup[rowIndex]
      ? field.fieldGroup[rowIndex].fieldGroup.find(f => f.key === key)
      : null;
  }

  containsCurrency(value) {
    if (!isNaN(value)) {
      return false;
    } else {
      return value?.includes('$');
    }
  }

  showDelete(subField) {
    let show = false;

    if (
      (this.field.key === 'exchange' && subField.isExchange) ||
      !this.field.fieldArray['props'].minEntries ||
      (this.field.fieldArray['props'].minEntries &&
        this.field.model?.length > 1)
    ) {
      show = true;
    }

    return show;
  }
}
